import React, { useState, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft,
  User,
  Mail,
  Lock,
  Save,
  Loader2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { rollenName } from '../lib/rollen';
import { SicherheitKarte } from '../components/zweifaktor/SicherheitKarte';

// Password strength checker
const checkPasswordStrength = (password) => {
  let strength = 0;
  let feedback = [];
  
  if (password.length >= 8) {
    strength += 25;
  } else {
    feedback.push('Mindestens 8 Zeichen');
  }
  
  if (/[a-z]/.test(password)) {
    strength += 25;
  } else {
    feedback.push('Kleinbuchstaben');
  }
  
  if (/[A-Z]/.test(password)) {
    strength += 25;
  } else {
    feedback.push('Großbuchstaben');
  }
  
  if (/[0-9]/.test(password) || /[^a-zA-Z0-9]/.test(password)) {
    strength += 25;
  } else {
    feedback.push('Zahlen oder Sonderzeichen');
  }
  
  let label = 'Sehr schwach';
  
  if (strength >= 100) {
    label = 'Stark';
  } else if (strength >= 75) {
    label = 'Gut';
  } else if (strength >= 50) {
    label = 'Mittel';
  } else if (strength >= 25) {
    label = 'Schwach';
  }
  
  return { strength, label, feedback };
};

const Profile = () => {
  const navigate = useNavigate();
  const { user, uebernehmeToken, isAdminLike } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const passwordStrength = useMemo(() => {
    return checkPasswordStrength(newPassword);
  }, [newPassword]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    if (passwordStrength.strength < 50) {
      toast.error('Bitte ein stärkeres Passwort wählen');
      return;
    }

    try {
      setSaving(true);
      const res = await authAPI.changePassword(currentPassword, newPassword);
      if (res?.data?.access_token) uebernehmeToken(res.data.access_token);
      toast.success('Passwort erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Ändern des Passworts'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
            Profil
          </h1>
          <p className="text-slate-500 mt-1">
            Verwalten Sie Ihre Kontodaten
          </p>
        </div>
      </div>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            Kontoinformationen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-blue-800" />
            </div>
            <div className="flex-1 space-y-2">
              <div>
                <p className="text-xl font-semibold text-slate-900">
                  {user?.first_name} {user?.last_name}
                </p>
                <div className="flex items-center gap-2 text-slate-500 mt-1">
                  <Mail className="h-4 w-4" />
                  {user?.email}
                </div>
              </div>
              <Badge className={user?.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'} data-testid="profile-role-badge">
                {rollenName(user?.role)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {isAdminLike() && <SicherheitKarte />}

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-5 w-5" />
            Passwort ändern
          </CardTitle>
          <CardDescription>
            Ändern Sie Ihr Passwort regelmäßig für mehr Sicherheit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {newPassword && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <Progress value={passwordStrength.strength} className="h-2 flex-1" />
                    <span className={`text-xs font-medium ${
                      passwordStrength.strength >= 75 ? 'text-green-600' :
                      passwordStrength.strength >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <p className="text-xs text-slate-500">
                      Fehlt: {passwordStrength.feedback.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Die Passwörter stimmen nicht überein</p>
              )}
            </div>

            <Button
              type="submit"
              className="bg-blue-800 hover:bg-blue-900"
              disabled={saving}
              data-testid="change-password-button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Passwort ändern
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
